// Shop sensors (jobq-api "SHOP ENVIRONMENT SENSORS", since 2026-10-26): the words and numbers the
// /sensors page shows, worked out from GET /sensors and GET /sensors/{id}/readings. Pure: no fetch,
// no React. The desktop page (pages/SensorsPage.jsx) and the phone screen
// (mobile/screens/SensorsScreen.jsx) both draw from these, so they always say the same thing.
//
// Which state wins (one sensor, one label):
//   1. switched off (is_active false)            -> "Not in use"
//   2. never reported                             -> "Waiting for its first reading"
//   3. no reading for offline_minutes (`online`)  -> "Offline: no reading for N min"
//   4. reporting, but no temperature              -> "Probe not reading"
//   5. temp_state low / high (JobQ's debounced state, 3 readings + 3 F hysteresis) -> "Too cold (below 35°F)" / "Too hot (above 110°F)"
//   6. otherwise                                  -> "Normal"
// Offline / probe go by the live answer (`online`, `last_temp_f`), not health_state: the API's
// health_state moves only on its minute tick, so right after the board comes back it can still say
// 'offline' for up to a minute while the reading on screen is fresh.

import { shopClock } from '../../utils/shopDate';

export const SENSORS_POLL_MS = 60000;

export const SENSOR_RANGES = Object.freeze([
  Object.freeze({ key: '24h', label: '24 h', hours: 24 }),
  Object.freeze({ key: '7d', label: '7 days', hours: 24 * 7 }),
  Object.freeze({ key: '30d', label: '30 days', hours: 24 * 30 }),
]);

/** The sensor picked: the one asked for, else the first one in use, else the first; null for none. */
export function pickSensor(sensors, wantedId) {
  const list = Array.isArray(sensors) ? sensors : [];
  return list.find((s) => s.sensor_id === wantedId)
    || list.find((s) => s.is_active !== false)
    || list[0]
    || null;
}

/** A finite number from a number or a numeric string, else null. */
export function num(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/** "41.3°F" (one decimal), "—" when there is no temperature. */
export function fmtTemp(f, digits = 1) {
  const n = num(f);
  return n === null ? '—' : `${n.toFixed(digits)}°F`;
}

/** A limit as typed: "35°F", "32.5°F". */
export function fmtLimit(f) {
  const n = num(f);
  if (n === null) return '—';
  return `${Number.isInteger(n) ? n : Number(n.toFixed(1))}°F`;
}

/** Minutes as words: "8 min", "1 h 5 min", "3 h", "2 days". */
export function durationText(minutes) {
  const m = num(minutes);
  if (m === null || m < 0) return '';
  const mins = Math.floor(m);
  if (mins < 90) return `${mins} min`;
  const h = Math.floor(mins / 60);
  if (h < 48) {
    const rest = mins % 60;
    return rest && h < 6 ? `${h} h ${rest} min` : `${h} h`;
  }
  return `${Math.floor(h / 24)} days`;
}

/** "just now", "3 min ago", "2 h ago". */
export function agoText(minutes) {
  const m = num(minutes);
  if (m === null) return '';
  if (m < 1) return 'just now';
  return `${durationText(m)} ago`;
}

/**
 * The sensor's state as the page says it: {tone, label, detail}. tone = ok | cold | hot | offline |
 * probe | muted (the page's colours). detail = one more plain sentence, or null.
 */
export function sensorStatus(s, now = new Date()) {
  if (!s) return { tone: 'muted', label: 'Unknown', detail: null };
  if (s.is_active === false) {
    return { tone: 'muted', label: 'Not in use', detail: 'Switched off: JobQ ignores this board.' };
  }
  if (!s.last_reading_at) {
    return { tone: 'muted', label: 'Waiting for its first reading', detail: 'The board has not reported to JobQ yet.' };
  }
  const since = (iso) => (iso ? `since ${shopClock(iso, now)}` : null);
  if (s.online === false) {
    const mins = num(s.minutes_since_reading);
    return {
      tone: 'offline',
      label: mins === null ? 'Offline' : `Offline: no reading for ${durationText(mins)}`,
      detail: 'Check the board’s power and Wi-Fi.',
    };
  }
  if (num(s.last_temp_f) === null) {
    return {
      tone: 'probe',
      label: 'Probe not reading',
      detail: s.health_state === 'probe' && s.health_state_since
        ? `The board is reporting but can't read its probe (${since(s.health_state_since)}). Check the probe cable.`
        : 'The board is reporting but its latest reading has no temperature. Check the probe cable.',
    };
  }
  const low = num(s.low_f);
  const high = num(s.high_f);
  if (s.temp_state === 'low') {
    return {
      tone: 'cold',
      label: low === null ? 'Too cold' : `Too cold (below ${fmtLimit(low)})`,
      detail: since(s.temp_state_since),
    };
  }
  if (s.temp_state === 'high') {
    return {
      tone: 'hot',
      label: high === null ? 'Too hot' : `Too hot (above ${fmtLimit(high)})`,
      detail: since(s.temp_state_since),
    };
  }
  // JobQ changes state only when 3 readings in a row agree; say so when the latest is already past a limit.
  const t = num(s.last_temp_f);
  let detail = null;
  if (low !== null && t < low) {
    detail = `The latest reading is under ${fmtLimit(low)}. JobQ alerts if the next readings agree.`;
  } else if (high !== null && t > high) {
    detail = `The latest reading is over ${fmtLimit(high)}. JobQ alerts if the next readings agree.`;
  }
  return { tone: 'ok', label: 'Normal', detail };
}

/** "2:14 PM · 1 min ago" (shop time). */
export function lastReadingText(s, now = new Date()) {
  if (!s || !s.last_reading_at) return 'No reading yet';
  const ago = agoText(s.minutes_since_reading);
  const clock = shopClock(s.last_reading_at, now);
  return ago ? `${clock} · ${ago}` : clock;
}

/** The alert limits in words: "Alerts below 35°F and above 110°F". */
export function limitsText(s) {
  if (!s) return '';
  const low = num(s.low_f);
  const high = num(s.high_f);
  let text;
  if (low !== null && high !== null) text = `Alerts below ${fmtLimit(low)} and above ${fmtLimit(high)}`;
  else if (low !== null) text = `Alerts below ${fmtLimit(low)} (no high limit)`;
  else if (high !== null) text = `Alerts above ${fmtLimit(high)} (no low limit)`;
  else text = 'No temperature limits set';
  return text;
}

/** "Offline alert after 15 min" + whether alerts are switched off. */
export function alertSettingsText(s) {
  if (!s) return '';
  const parts = [];
  const m = num(s.offline_minutes);
  if (m !== null) parts.push(`offline alert after ${m} min`);
  if (s.alerts_on === false) parts.push('ALERTS SWITCHED OFF');
  return parts.join(' · ');
}

// ── Alerts ──

const ALERT_KINDS = {
  low: { label: 'Too cold', tone: 'cold', problem: true },
  high: { label: 'Too hot', tone: 'hot', problem: true },
  temp_ok: { label: 'Back to normal', tone: 'ok', problem: false },
  offline: { label: 'Offline', tone: 'offline', problem: true },
  online: { label: 'Reporting again', tone: 'ok', problem: false },
  probe: { label: 'Probe not reading', tone: 'probe', problem: true },
  probe_ok: { label: 'Probe reading again', tone: 'ok', problem: false },
  test: { label: 'Test alert', tone: 'muted', problem: false },
};

/** {label, tone, problem} for an alert kind; an unknown kind reads as itself. */
export function alertKind(kind) {
  return ALERT_KINDS[kind] || { label: String(kind || 'Alert'), tone: 'muted', problem: false };
}

const listOf = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === 'string' && x.trim()) : []);

/** A plain sentence from an error the API stored ("not sent: outside prod" -> "Not sent: …"). */
export function errorText(e) {
  const s = String(e || '').trim();
  if (/^not sent: outside prod$/i.test(s)) return 'Not sent: this is not the live JobQ (alerts only go out from jobqworks.com).';
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

/**
 * Who an alert reached: {sent, errors}. sent = "Texted Mike, Jesse · Pushed Mike", "Nobody was
 * texted or pushed", or "Sending…" while the API has not sent it yet; errors = sentences.
 */
export function alertDelivery(a) {
  if (!a) return { sent: '', errors: [] };
  const texted = listOf(a.texted);
  const pushed = listOf(a.pushed);
  const errors = listOf(a.errors).map(errorText);
  const parts = [];
  if (texted.length) parts.push(`Texted ${texted.join(', ')}`);
  if (pushed.length) parts.push(`Pushed ${pushed.join(', ')}`);
  let sent = parts.join(' · ');
  if (!sent) sent = a.sent_at || errors.length ? 'Nobody was texted or pushed' : 'Sending…';
  return { sent, errors };
}

/** The answer of POST /sensors/{id}/test_alert in words. */
export function testAlertSummary(body) {
  if (!body) return '';
  const d = alertDelivery({ ...body, sent_at: 'x' });
  return [`Sent: "${body.text || ''}"`, d.sent, ...d.errors].filter(Boolean).join('\n');
}

// ── The chart ──

const tms = (iso) => {
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : null;
};

/**
 * Chart rows from GET /sensors/{id}/readings: [{t (ms), f, band: [lo, hi] | null}], oldest first.
 * Where readings stop (the board offline) a null row is put in, so the line breaks instead of
 * drawing straight across the gap: raw readings come a minute apart, so > 5 min is a gap; bucketed
 * rows come one bucket apart, so > 2.5 buckets is a gap.
 */
export function chartRows(body) {
  const pts = body && Array.isArray(body.points) ? body.points : [];
  const bucket = num(body && body.bucket_seconds) || 0;
  const rows = pts
    .map((p) => {
      const t = tms(p && p.t);
      if (t === null) return null;
      const lo = num(p.lo);
      const hi = num(p.hi);
      return { t, f: num(p.f), band: lo !== null && hi !== null ? [lo, hi] : null };
    })
    .filter(Boolean)
    .sort((a, b) => a.t - b.t);
  const gap = bucket > 0 ? bucket * 1000 * 2.5 : 5 * 60 * 1000;
  const out = [];
  rows.forEach((r, i) => {
    if (i > 0 && r.t - rows[i - 1].t > gap) out.push({ t: rows[i - 1].t + 1, f: null, band: null, gap: true });
    out.push(r);
  });
  return out;
}

/** How close (°F) the readings must come to a limit for the chart to stretch to show it. */
export const LIMIT_IN_VIEW_F = 20;

const readingValues = (rows) => {
  const vals = [];
  (rows || []).forEach((r) => {
    if (num(r.f) !== null) vals.push(r.f);
    if (r.band) vals.push(r.band[0], r.band[1]);
  });
  return vals;
};

/**
 * Which limits the chart draws: {low, high}, each true when the readings come within
 * LIMIT_IN_VIEW_F of it (a 110°F high limit on an October day of 30-60°F would squash the line into
 * a third of the chart; the legend then names it instead). With no readings both show.
 */
export function limitsInView(rows, low, high) {
  const vals = readingValues(rows);
  const lo = num(low);
  const hi = num(high);
  if (!vals.length) return { low: lo !== null, high: hi !== null };
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  return {
    low: lo !== null && lo >= min - LIMIT_IN_VIEW_F,
    high: hi !== null && hi <= max + LIMIT_IN_VIEW_F,
  };
}

/** The y-axis: every reading and the limits in view (limitsInView), padded, on 5°F steps. */
export function yDomain(rows, low, high) {
  const vals = readingValues(rows);
  const shown = limitsInView(rows, low, high);
  if (shown.low) vals.push(num(low));
  if (shown.high) vals.push(num(high));
  if (!vals.length) return [30, 90];
  const lo = Math.floor((Math.min(...vals) - 3) / 5) * 5;
  const hi = Math.ceil((Math.max(...vals) + 3) / 5) * 5;
  return [lo, hi === lo ? lo + 5 : hi];
}

/** Alerts as chart marks: [{t, kind, label, tone}] inside [from, to], oldest first. */
export function alertMarks(alerts, from, to) {
  return (Array.isArray(alerts) ? alerts : [])
    .map((a) => ({ t: tms(a && a.created_at), kind: a && a.kind, ...alertKind(a && a.kind) }))
    .filter((m) => m.t !== null && (from === undefined || m.t >= from) && (to === undefined || m.t <= to))
    .sort((a, b) => a.t - b.t);
}

/** An x-axis tick in shop time: "2 PM" over a day, "10/24" beyond. */
export function tickText(ms, hours) {
  const opts = hours <= 48
    ? { hour: 'numeric', minute: '2-digit', timeZone: 'America/Chicago' }
    : { month: 'numeric', day: 'numeric', timeZone: 'America/Chicago' };
  return new Date(ms).toLocaleString('en-US', opts).replace(/[  ]/g, ' ');
}

const CENTRAL_HOUR = new Intl.DateTimeFormat('en-US', { hour: 'numeric', hourCycle: 'h23', timeZone: 'America/Chicago' });

/**
 * X-axis ticks for a multi-day range: shop-time (Central) midnights inside [start, end], thinned to
 * at most `max` (every 2nd, 3rd… day). null for ranges of 48 h or less (the chart picks hours itself).
 */
export function dayTicks(start, end, hours, max = 8) {
  if (!(hours > 48) || !(end > start)) return null;
  const mids = [];
  for (let t = Math.ceil(start / 3600000) * 3600000; t <= end; t += 3600000) {
    if (Number(CENTRAL_HOUR.format(new Date(t))) === 0) mids.push(t);
  }
  const step = Math.max(1, Math.ceil(mids.length / max));
  return mids.filter((_, i) => i % step === 0);
}

/** What the chart's rows are: "every reading" / "15-min averages, low–high shaded". */
export function resolutionText(bucketSeconds) {
  const b = num(bucketSeconds) || 0;
  if (!b) return 'every reading';
  if (b < 3600) return `${Math.round(b / 60)}-min averages, low–high shaded`;
  return `${Math.round(b / 3600)}-hour averages, low–high shaded`;
}

// ── Admin forms ──

/** The form's starting values for a sensor: strings, '' = that limit off. */
export function settingsForm(s) {
  return {
    name: (s && s.name) || '',
    location: (s && s.location) || '',
    low_f: s && num(s.low_f) !== null ? String(num(s.low_f)) : '',
    high_f: s && num(s.high_f) !== null ? String(num(s.high_f)) : '',
    offline_minutes: s && num(s.offline_minutes) !== null ? String(num(s.offline_minutes)) : '15',
    alerts_on: s ? s.alerts_on !== false : true,
  };
}

function limitValue(text, label) {
  const t = String(text ?? '').trim();
  if (!t) return { value: null };
  const n = Number(t);
  if (!Number.isFinite(n)) return { error: `${label} must be a number of degrees F (or blank for no limit).` };
  return { value: Math.round(n * 10) / 10 };
}

function minutesValue(text) {
  const t = String(text ?? '').trim();
  const n = Number(t);
  if (!t || !Number.isInteger(n) || n < 5 || n > 240) return { error: 'Offline minutes must be a whole number from 5 to 240.' };
  return { value: n };
}

/**
 * The PATCH body for a settings form: only what changed. {body} (null = nothing changed) or
 * {error}. A limit left blank is sent as null (that side off). Whether the limits are far enough
 * apart is JobQ's rule (it answers 400 with the reason): never checked here.
 */
export function settingsPatch(sensor, form) {
  const name = String(form.name || '').trim();
  if (!name) return { error: 'A sensor needs a name.' };
  const low = limitValue(form.low_f, 'The low limit');
  if (low.error) return { error: low.error };
  const high = limitValue(form.high_f, 'The high limit');
  if (high.error) return { error: high.error };
  const mins = minutesValue(form.offline_minutes);
  if (mins.error) return { error: mins.error };
  const location = String(form.location || '').trim() || null;
  const next = { name, location, low_f: low.value, high_f: high.value, offline_minutes: mins.value, alerts_on: !!form.alerts_on };
  const was = {
    name: sensor.name || '',
    location: sensor.location || null,
    low_f: num(sensor.low_f),
    high_f: num(sensor.high_f),
    offline_minutes: num(sensor.offline_minutes),
    alerts_on: sensor.alerts_on !== false,
  };
  const body = {};
  Object.keys(next).forEach((k) => { if (next[k] !== was[k]) body[k] = next[k]; });
  return { body: Object.keys(body).length ? body : null };
}

/** The POST /sensors body for "Register a sensor": {body} or {error}. */
export function newSensorBody(form) {
  const name = String(form.name || '').trim();
  if (!name) return { error: 'A sensor needs a name.' };
  const low = limitValue(form.low_f, 'The low limit');
  if (low.error) return { error: low.error };
  const high = limitValue(form.high_f, 'The high limit');
  if (high.error) return { error: high.error };
  const mins = minutesValue(form.offline_minutes);
  if (mins.error) return { error: mins.error };
  const body = { name, low_f: low.value, high_f: high.value, offline_minutes: mins.value };
  const location = String(form.location || '').trim();
  if (location) body.location = location;
  return { body };
}

/** The register form's defaults (Mike's: below 35°F, above 110°F, offline after 15 min). */
export const NEW_SENSOR_FORM = Object.freeze({ name: '', location: '', low_f: '35', high_f: '110', offline_minutes: '15' });
